/**
 * The frame every screen renders in. A2 `index.tsx:46-83`.
 *
 * - `h-full`, and nothing here scrolls: each screen owns its inner scroll
 *   region (`ui/controls` `Scroll`), so the page never scrolls as a body.
 * - Focus mode: on `screen=workout` neither the sidebar nor the tab bar is
 *   drawn.
 * - `--coach-dock` is `440px` while `overlay=coach` on a ≥1024 px viewport,
 *   otherwise `0px`. It is written on `<html>`, not only on this element,
 *   because Base UI portals render into `<body>`: a sheet, a dialog or the
 *   toast (T14) reads it there to stay left of the docked trainer. The toast
 *   viewport is one of those portals and its `ToastProvider` now sits ABOVE
 *   the session gate (`app/shell.tsx`, T46), outside this component — sign-in
 *   and onboarding need the channel too. It still reads `--coach-dock` from
 *   `<html>`, which is exactly why that mirroring exists.
 * - The workout makes room for the docked trainer (`lg:pr-(--coach-dock)`):
 *   logging a set while reading the answer is the point. Other screens keep
 *   their width and the panel sits over them, as in A2.
 * - The base screen arrives as the `screen` PROP and the layers as
 *   `children`. It used to be the first child, with the rest taken as layers,
 *   so a provider or a bridge inserted above the screen silently became "the
 *   screen" and took the fade key. Naming it is what makes that impossible.
 * - The screens move inside `app/screen-stack.tsx` (`D193`), which keeps
 *   every visited one mounted; this frame only slides the whole stack a
 *   little left while a full-screen layer covers it (`useUnderlayShift`).
 *   The overlay and the sheet render outside that element, so switching
 *   screens with the trainer docked never remounts the trainer.
 * - The trainer covers the tab bar on a phone ("no tab bar under the chat"),
 *   so the bar is not drawn while `overlay=coach`, and no padding is added
 *   for it at any time: a screen's `Scroll` keeps its content clear of the
 *   bar itself.
 * - `<WidgetSnapshotWriter />` (`D280`), beside the runtime below, only in a shell with
 *   home-screen widgets.
 * - `<WorkoutRuntime />` (T53) is mounted here, once: the rest alarm, the
 *   buzz and keep-awake must survive minimising the workout to Today. So is
 *   `<ForgottenWorkoutAsk />` (`D245`): a workout left open is asked about
 *   over whatever screen the app opens on.
 */
import { MotionConfig, useReducedMotion } from "motion/react";
import {
  type CSSProperties,
  type ReactNode,
  type RefObject,
  useEffect,
  useLayoutEffect,
  useRef,
} from "react";
import { useAppSearch } from "../../app/layers";
import { screenOf } from "../../app/search";
import { cx } from "../../lib/cx";
import { widgetsAvailable } from "../../native";
import { coverage, UNDERLAY_SHIFT_PERCENT } from "../../ui/overlay/underlay";
import { ConnectionsRuntime } from "../connections/runtime";
import { WidgetSnapshotWriter } from "../widgets/writer";
import { ForgottenWorkoutAsk } from "../workout/screens/forgotten-ask";
import { WorkoutRuntime } from "../workout/store";
import { CoachBubble } from "./coach-bubble";
import { coachDockWidth, useIsDesktop } from "./navigation";
import { OfflineIndicator } from "./offline-indicator";
import { useSearchShortcut } from "./search-shortcut";
import { Sidebar } from "./sidebar";
import { TabBar } from "./tab-bar";

/**
 * Slides the screens a little left while a full-screen layer covers them,
 * and back as it leaves or as a Back gesture pulls it away (`D193`,
 * `ui/overlay/underlay.ts`). Written straight to the element on every frame
 * of the motion value, so nothing re-renders. Off on desktop, where the
 * layers are docked or centred and the screen beside them must stay put, and
 * under reduced motion and the shot harness.
 */
function useUnderlayShift(
  ref: RefObject<HTMLDivElement | null>,
  enabled: boolean
): void {
  useEffect(() => {
    const element = ref.current;
    if (element === null) {
      return;
    }
    const apply = (value: number) => {
      const off =
        !enabled || document.documentElement.hasAttribute("data-shot");
      element.style.transform =
        off || value === 0
          ? ""
          : `translateX(${-UNDERLAY_SHIFT_PERCENT * value}%)`;
    };
    apply(coverage.get());
    const stop = coverage.on("change", apply);
    return () => {
      stop();
      element.style.transform = "";
    };
  }, [ref, enabled]);
}

/** Mirrors `--coach-dock` onto `<html>` so portalled layers inherit it. */
function useCoachDock(width: string): void {
  useLayoutEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--coach-dock", width);
    return () => {
      root.style.removeProperty("--coach-dock");
    };
  }, [width]);
}

export interface AppLayoutProps {
  /** The layers over the screen: the overlay and the sheet. */
  children?: ReactNode;
  /** The base screen (`screen=`). */
  screen: ReactNode;
}

export function AppLayout({ screen, children }: AppLayoutProps) {
  const search = useAppSearch();
  const current = screenOf(search);
  const focus = current === "workout";
  const coachOpen = search.overlay === "coach";
  const desktop = useIsDesktop();
  const reduced = useReducedMotion();
  const dock = coachDockWidth(coachOpen, desktop);
  useCoachDock(dock);
  useSearchShortcut();
  const underlay = useRef<HTMLDivElement>(null);
  useUnderlayShift(underlay, !(desktop || reduced));

  const showSidebar = desktop && !focus;
  const showTabBar = !(desktop || focus || coachOpen);

  return (
    <MotionConfig reducedMotion="user">
      <WorkoutRuntime />
      {widgetsAvailable() ? <WidgetSnapshotWriter /> : null}
      <ConnectionsRuntime />
      <ForgottenWorkoutAsk />
      <div
        className="relative h-full bg-canvas font-ui text-ink"
        data-focus={focus ? "" : undefined}
        data-shell
        style={{ "--coach-dock": dock } as CSSProperties}
      >
        <div className="flex h-full overflow-hidden">
          {showSidebar ? <Sidebar /> : null}
          <main
            className={cx(
              "relative h-full min-w-0 flex-1",
              focus && "lg:pr-(--coach-dock)"
            )}
          >
            <OfflineIndicator />
            <div className="h-full" data-underlay="" ref={underlay}>
              {screen}
            </div>
          </main>
        </div>
        {showTabBar ? <TabBar /> : null}
        {/* The trainer's door on a phone, over every screen (`D218`). */}
        {desktop || coachOpen ? null : <CoachBubble />}
        {children}
      </div>
    </MotionConfig>
  );
}
